import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { App, Form, Input } from 'antd'
import { createSchemaFieldRule } from 'antd-zod'
import { BUSINESS_DEFAULT_TAG_COLOR } from '@super-mega/server/contracts/business'
import {
  expenseBusinessUpsertSchema,
  type ExpenseBusinessUpsertInput
} from '@super-mega/server/contracts/expense-business'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { BusinessTagColorPicker } from '../business/business-tag-color-picker.tsx'

export function ExpenseBusinessEditor({
  editing,
  onClose,
  onSaved
}: {
  editing: (ExpenseBusinessUpsertInput & { id: number }) | 'new'
  onClose: () => void
  onSaved: (saved: { id: number }) => Promise<unknown>
}) {
  const [open, setOpen] = useState(true)
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [form] = Form.useForm<ExpenseBusinessUpsertInput>()
  const save = useMutation(trpc.expenseBusiness.upsert.mutationOptions())
  const rule = createSchemaFieldRule(expenseBusinessUpsertSchema)
  return (
    <ModalForm<ExpenseBusinessUpsertInput>
      name="expense-business-editor"
      open={open}
      onOpenChange={setOpen}
      form={form}
      layout="vertical"
      width="36rem"
      title={editing === 'new' ? '新建支出业务' : '编辑支出业务'}
      initialValues={editing === 'new' ? { color: BUSINESS_DEFAULT_TAG_COLOR } : editing}
      modalProps={{
        destroyOnHidden: true,
        mask: { closable: false },
        afterClose: onClose,
        onCancel: () => setOpen(false)
      }}
      submitter={{
        resetButtonProps: { onClick: () => setOpen(false) },
        submitButtonProps: { loading: save.isPending },
        searchConfig: { submitText: '保存' }
      }}
      onFinish={async (values) => {
        try {
          const saved = await save.mutateAsync({
            id: editing === 'new' ? undefined : editing.id,
            data: expenseBusinessUpsertSchema.parse(values)
          })
          await onSaved(saved)
          setOpen(false)
          void message.success('支出业务已保存')
          return true
        } catch (error) {
          void message.error(getErrorMessage(error, '保存失败'))
          return false
        }
      }}
    >
      <Form.Item name="name" label="支出业务名称" required rules={[rule]}>
        <Input maxLength={255} placeholder="如：工资、业绩、佣金、水费、电费、租赁费用" />
      </Form.Item>
      <Form.Item name="color" label="标签颜色" required rules={[rule]}>
        <BusinessTagColorPicker />
      </Form.Item>
      <Form.Item name="note" label="备注" rules={[rule]}>
        <Input.TextArea rows={3} maxLength={5000} />
      </Form.Item>
    </ModalForm>
  )
}
